// src/schemas/renderer.ts
var PPTX_RENDERER_IDS = ["pptxgenjs", "office-open"];
var DEFAULT_PPTX_RENDERER_ID = "pptxgenjs";
function pptxComponentDefinitionName(renderer) {
  return `PptxComponentDefinition_${renderer}`;
}
function pptxPropsSchemaForRenderer(componentName, schema, renderer) {
  const copy = cloneSchema(schema);
  const properties = objectProperties(copy);
  if (renderer === "pptxgenjs") {
    if (componentName === "slide") delete properties?.transition;
    return copy;
  }
  switch (componentName) {
    case "image":
      for (const key of ["svg", "sizing", "rotate", "rounding", "hyperlink"]) {
        delete properties?.[key];
      }
      break;
    case "shape":
      delete properties?.flipV;
      break;
    case "table":
      for (const key of [
        "autoPage",
        "autoPageRepeatHeader",
        "margin",
        "borderRadius"
      ]) {
        delete properties?.[key];
      }
      pruneOfficeOpenTableCells(copy);
      break;
  }
  syncRequired(copy);
  return copy;
}
function collectPptxRendererErrors(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];
  const root = data;
  const renderer = root.renderer ?? DEFAULT_PPTX_RENDERER_ID;
  if (!PPTX_RENDERER_IDS.includes(renderer)) {
    return [
      {
        path: "/renderer",
        message: `Invalid renderer "${String(renderer)}". Expected "pptxgenjs" or "office-open".`,
        code: "invalid_value"
      }
    ];
  }
  const errors = [];
  const unsupported = (path, feature) => {
    errors.push({
      path,
      message: `The "${renderer}" renderer does not support ${feature}.`,
      code: "unsupported_renderer_feature"
    });
  };
  const visit = (node, path) => {
    if (Array.isArray(node)) {
      node.forEach((entry, index) => visit(entry, `${path}/${index}`));
      return;
    }
    if (!node || typeof node !== "object") return;
    const props = node.props;
    if (renderer === "pptxgenjs") {
      if (node.name === "slide" && props?.transition !== void 0) {
        unsupported(`${path}/props/transition`, "slide transitions");
      }
    } else {
      if (node.name === "chart" && props?.type === "bubble") {
        unsupported(`${path}/props/type`, "bubble charts");
      }
      if (node.name === "image") {
        const fields = {
          svg: "SVG images",
          sizing: "image cropping",
          rotate: "image transforms",
          rounding: "image rounding",
          hyperlink: "element hyperlinks"
        };
        for (const [field, feature] of Object.entries(fields)) {
          if (props?.[field] !== void 0) {
            unsupported(`${path}/props/${field}`, feature);
          }
        }
      }
      if (node.name === "shape" && props?.flipV !== void 0) {
        unsupported(`${path}/props/flipV`, "vertical flipping");
      }
      if (node.name === "table") {
        const fields = {
          autoPage: "table auto-pagination",
          autoPageRepeatHeader: "table auto-pagination",
          margin: "table insets",
          borderRadius: "rounded table corners"
        };
        for (const [field, feature] of Object.entries(fields)) {
          if (props?.[field] !== void 0) {
            unsupported(`${path}/props/${field}`, feature);
          }
        }
        if (Array.isArray(props?.rows)) {
          props.rows.forEach((row, rowIndex) => {
            if (!Array.isArray(row)) return;
            row.forEach((cell, cellIndex) => {
              if (!cell || typeof cell !== "object") return;
              for (const [field, feature] of [
                ["colspan", "merged table cells"],
                ["rowspan", "merged table cells"],
                ["margin", "table insets"]
              ]) {
                if (cell[field] !== void 0) {
                  unsupported(
                    `${path}/props/rows/${rowIndex}/${cellIndex}/${field}`,
                    feature
                  );
                }
              }
            });
          });
        }
      }
    }
    for (const [key, value] of Object.entries(node)) {
      if (key !== "renderer") visit(value, `${path}/${key}`);
    }
  };
  visit(root, "");
  return errors;
}
function pruneOfficeOpenTableCells(schema) {
  const rows = objectProperties(schema)?.rows;
  const cellUnion = rows?.items?.items;
  const branches = cellUnion?.anyOf;
  if (!Array.isArray(branches)) return;
  for (const branch of branches) {
    const properties = objectProperties(branch);
    if (!properties?.text) continue;
    delete properties.colspan;
    delete properties.rowspan;
    delete properties.margin;
    syncRequired(branch);
  }
}
function objectProperties(schema) {
  if (!schema || typeof schema !== "object") return void 0;
  return schema.properties;
}
function syncRequired(schema) {
  const properties = objectProperties(schema);
  const required = schema.required;
  if (!properties || !required) return;
  const next = required.filter((key) => key in properties);
  if (next.length > 0) schema.required = next;
  else delete schema.required;
}
function cloneSchema(value) {
  if (Array.isArray(value)) return value.map(cloneSchema);
  if (!value || typeof value !== "object") return value;
  const copy = Object.create(Object.getPrototypeOf(value));
  for (const key of Reflect.ownKeys(value)) {
    copy[key] = cloneSchema(value[key]);
  }
  return copy;
}

export {
  PPTX_RENDERER_IDS,
  DEFAULT_PPTX_RENDERER_ID,
  pptxComponentDefinitionName,
  pptxPropsSchemaForRenderer,
  collectPptxRendererErrors
};
//# sourceMappingURL=chunk-Y2ODB5VW.js.map